import React, { useState } from 'react';
import classNames from 'classnames';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import {
  extractDigits,
  NeuroFinancialAssistanceDto,
  neuroFinancialAssistanceFields,
} from '@tempus/patient-forms-service-shared';
import { PortalViewsEnum } from '@tempus/portal-service-shared';
import { NavigationFormProps } from '../../../custom-to-do-list/components/NavigatorChecklist';
import Form from '../../shared/Form';
import Logo from '../../shared/Logo';
import { deleteUnmetConditionalFields, deleteFalsyStringFields, combineFormConfigs } from '../../shared/utils';
import CheckYourEmailAndPhone from '../../shared/CheckYourEmailAndPhone';
import NeuroStandaloneFormUnknownError from '../../shared/psychiatry/NeuroStandaloneFormUnknownError';
import PatientStandaloneFormAuthenticationManager, {
  PatientStandaloneFormAuthenticationValues,
  StandaloneFormAuthenticationError,
} from '../../shared/PatientStandaloneFormAuthenticationFlow';
import standaloneStyles from '../../shared/standalone-styles.module.css';
import { PatientFormsTitleInjection } from '../../../custom-to-do-list/components/PatientFormsTitleInjection';
import fetchHelper from '../../../../utils';
import config from '../../../../../config';
import { CachedFile } from '../../../../portal/components/UploadZone';
import { buildBase64DataUrl } from '../../../../portal/utils/getBase64File';
import NeuroFinancialAssistanceForm, { neuroFinancialAssistanceFieldsFrontend } from './NeuroFinancialAssistanceForm';
import { AuthenticationType } from '../../../../api/AuthenticationType';
import { FinancialAssistanceStatuses } from 'requisition-service-shared';
import Loader from '@tcl-boron-prefabs/loader';
import logger from '../../../../../utils/logger';
import {
  NeuroFinancialAssistanceSubmissionError,
  NeuroFinancialAssistanceSubmitted,
  NeuroFinancialAssistanceAlreadySubmitted,
} from '../components';

export const NEURO_FINANCIAL_ASSISTANCE_KEY = 'NEUROLOGY_FINANCIAL_ASSISTANCE';
export const NEURO_FINANCIAL_ASSISTANCE_TITLE = 'Neurology Financial Assistance';

type NeuroFinancialAssistanceStartFormResponse = {
  faStatus: FinancialAssistanceStatuses | null;
};

type NeuroFinancialAssistanceSubmitFormResponse = {
  faStatus: FinancialAssistanceStatuses | null;
};

type NeuroFinancialAssistanceStartFormProps = PatientStandaloneFormAuthenticationValues &
  NeuroFinancialAssistanceStartFormResponse;

const NeuroFinancialAssistanceWrapper: React.FunctionComponent<NavigationFormProps> = (props) => {
  const [showSuccessAlert, setShowAlert] = useState<boolean>(false);
  const [startFormProps, setStartFormProps] = useState<NeuroFinancialAssistanceStartFormProps | null>(null);
  const [cachedFile, setCachedFile] = useState<CachedFile | undefined>();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isGenericError, setIsGenericError] = useState<boolean>(false);
  const [isSubmissionError, setIsSubmissionError] = useState<boolean>(false);
  const [isStartError, setIsStartError] = useState<boolean>(false);
  const [isLinkSent, setIsLinkSent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [faStatus, setFaStatus] = useState<FinancialAssistanceStatuses | undefined>();

  const onSuccessfulAuthentication = async (patientLookupValues: PatientStandaloneFormAuthenticationValues) => {
    setIsLinkSent(false);
    setIsGenericError(false);
    setIsSubmissionError(false);
    setIsStartError(false);

    const {
      patientId,
      patientFirstName,
      patientLastName,
      patientDateOfBirth,
      patientEmail,
      patientPhoneNumber,
    } = patientLookupValues;

    try {
      setIsLoading(true);
      const data = await fetchHelper<NeuroFinancialAssistanceStartFormResponse>(`/form/standalone/start`, {
        baseUrl: config.patientWebServiceBaseUrl,
        method: 'POST',
        headerOverrides: {
          'x-selected-portal-view': PortalViewsEnum.PSYCHIATRY,
        },
        body: {
          formType: NEURO_FINANCIAL_ASSISTANCE_KEY,
          patientId,
          patientFirstName,
          patientLastName,
          patientDateOfBirth,
          patientEmail,
          patientPhoneNumber: extractDigits(patientPhoneNumber),
        },
        authenticationType: AuthenticationType.MANUAL,
        authToken: patientLookupValues.identityVerificationToken,
      });
      setStartFormProps({
        ...patientLookupValues,
        ...data,
      });
    } catch (error) {
      setIsStartError(true);
      setStartFormProps(null);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: any) => {
    if (!startFormProps) {
      throw new Error('Patient lookup response is required for submission');
    }

    const formConfig = combineFormConfigs(neuroFinancialAssistanceFieldsFrontend, neuroFinancialAssistanceFields);
    deleteUnmetConditionalFields(values, formConfig);
    deleteFalsyStringFields(values);

    const dto: NeuroFinancialAssistanceDto = {
      ...values,
      extenuatingCircumstances: values.extenuatingCircumstances || [],
      patientFirstName: startFormProps.patientFirstName,
      patientLastName: startFormProps.patientLastName,
      patientDateOfBirth: startFormProps.patientDateOfBirth,
      patientEmail: startFormProps.patientEmail,
      patientPhoneNumber: extractDigits(startFormProps.patientPhoneNumber),
      acceptableProofOfHouseholdIncome: buildBase64DataUrl(cachedFile?.type ?? '', cachedFile?.content ?? ''),
    };

    try {
      const response = await fetchHelper<NeuroFinancialAssistanceSubmitFormResponse>(`/form/standalone/submit`, {
        method: 'POST',
        baseUrl: config.patientWebServiceBaseUrl,
        headerOverrides: {
          'x-selected-portal-view': PortalViewsEnum.PSYCHIATRY,
        },
        body: {
          formType: NEURO_FINANCIAL_ASSISTANCE_KEY,
          patientId: startFormProps.patientId,
          data: dto,
        },
        authenticationType: AuthenticationType.MANUAL,
        authToken: startFormProps?.identityVerificationToken,
      });

      setFaStatus(response?.faStatus ?? undefined);
      setIsSubmitted(true);
    } catch (e: any) {
      logger.error(e, 'Error submitting Neuro Financial Assistance form');
      setIsSubmissionError(true);
    }
  };

  const showFormState = startFormProps && !startFormProps.faStatus;
  const isAlreadySubmitted = startFormProps && startFormProps.faStatus;

  const clearForm = () => {
    setStartFormProps(null);
    setIsLinkSent(false);
    setIsGenericError(false);
    setIsStartError(false);
  };

  return (
    <PatientFormsTitleInjection>
      <div>
        <div className={classNames(typography.body, standaloneStyles.screen)}>
          <div className={standaloneStyles.header}>
            <div className={standaloneStyles.headerLogo}>
              <Logo />
            </div>
          </div>
          <div className={standaloneStyles.content}>
            <div className={standaloneStyles.titleContainer}>
              <p className={standaloneStyles.title}>{NEURO_FINANCIAL_ASSISTANCE_TITLE}</p>
              {startFormProps?.referenceId && (
                <p className={standaloneStyles.subtitle}>
                  Form Reference ID: <strong>{startFormProps.referenceId}</strong>
                </p>
              )}
            </div>
            {isLinkSent && <CheckYourEmailAndPhone variant="fa" />}
            <PatientStandaloneFormAuthenticationManager
              onAuthenticationSuccess={onSuccessfulAuthentication}
              onAuthenticationError={() => {
                clearForm();
                setIsStartError(true);
              }}
              onClear={clearForm}
              onSendTokenizedLinkDispatched={() => {
                setIsLinkSent(true);
                setIsGenericError(false);
                setIsSubmissionError(false);
                setIsStartError(false);
              }}
              onSendTokenizedLinkError={() => {
                clearForm();
                setIsLinkSent(false);
                setIsGenericError(true);
                setIsSubmissionError(false);
                setIsStartError(false);
              }}
              formType={NEURO_FINANCIAL_ASSISTANCE_KEY}
            />
            {isGenericError ? (
              <NeuroStandaloneFormUnknownError referenceId={startFormProps?.referenceId} />
            ) : isStartError ? (
              <StandaloneFormAuthenticationError />
            ) : isSubmissionError ? (
              <NeuroFinancialAssistanceSubmissionError
                referenceId={startFormProps?.referenceId}
                onRetry={() => {
                  setIsSubmissionError(false);
                  setIsSubmitted(false);
                  setCachedFile(undefined);
                }}
              />
            ) : isSubmitted ? (
              <NeuroFinancialAssistanceSubmitted approvalStatus={faStatus} referenceId={startFormProps?.referenceId} />
            ) : isAlreadySubmitted ? (
              <NeuroFinancialAssistanceAlreadySubmitted referenceId={startFormProps?.referenceId} />
            ) : isLoading ? (
              <div className={standaloneStyles.loaderContainer}>
                <Loader />
              </div>
            ) : showFormState ? (
              <Form onSubmit={onSubmit}>
                <NeuroFinancialAssistanceForm
                  showAlert={showSuccessAlert}
                  setShowAlert={setShowAlert}
                  cachedFile={cachedFile}
                  setCachedFile={setCachedFile}
                  {...props}
                />
              </Form>
            ) : null}
          </div>
        </div>
      </div>
    </PatientFormsTitleInjection>
  );
};

export default NeuroFinancialAssistanceWrapper;
